// ============================================================================
// First Mile vocabulary — the labels and rate maths shared by the GRN / Pick List sections,
// their per-warehouse tables and the drill-down drawers, so a warehouse or a bucket reads and
// counts the same everywhere. Mirrors the real backend summary (board_lane=first_mile, see
// control_tower.api.ts).
//
// Horamavu and Warehouse Now are the same physical warehouse, so "horamavu" is one entry
// labelled "Horamavu/Warehouse Now", not two.
// ============================================================================
import {
  EscalationDrilldownKey,
  FirstMileExclusionKey,
  FirstMileGrnCell,
  FirstMilePickCell,
  FirstMilePicklistEscalationBucketKey,
  PicklistEscalationDrilldownKey,
} from "./control_tower.api";
import { FirstMileWarehouseKey } from "./types";

// Dock (receiving warehouse) labels — keyed by the backend dock value, for the drill-down table.
export const FIRST_MILE_DOCK_LABEL: Record<FirstMileWarehouseKey, string> = {
  rebound: "Rebound",
  horamavu: "Horamavu/Warehouse Now",
  hyderabad: "Hyderabad",
};

export const FIRST_MILE_WAREHOUSES: FirstMileWarehouseKey[] = ["rebound", "horamavu", "hyderabad"];

// The three derived exclusions. Long labels head a drawer; short ones fit a tile / table chip.
export const EXCLUSION_LABEL: Record<FirstMileExclusionKey, string> = {
  po_after_6pm: "PO raised after 6 PM",
  heavy_po: "Heavy cargo (> 700 kg)",
  large_tile: "Large tile (> 4x2)",
};

export const EXCLUSION_SHORT: Record<FirstMileExclusionKey, string> = {
  po_after_6pm: "After 6 PM",
  heavy_po: "Heavy > 700kg",
  large_tile: "Tile > 4x2",
};

export const EXCLUSION_ORDER: FirstMileExclusionKey[] = ["po_after_6pm", "heavy_po", "large_tile"];

// Every drillable figure in the GRN section.
export const ESCALATION_DRILLDOWN_LABEL: Record<EscalationDrilldownKey, string> = {
  all: "All GRN escalations & exclusions",
  excluded: "Excluded GRNs",
  vendor_to_send: "Vendor to Send",
  ...EXCLUSION_LABEL,
};

// The two picklist SLA buckets.
export const PICKLIST_BUCKET_LABEL: Record<FirstMilePicklistEscalationBucketKey, string> = {
  pending_over_30m: "Not picked > 30 min",
  slow_pick_over_30m: "Picked, but took > 30 min",
};

export const PICKLIST_BUCKET_SHORT: Record<FirstMilePicklistEscalationBucketKey, string> = {
  pending_over_30m: "Not picked > 30m",
  slow_pick_over_30m: "Picked > 30m",
};

export const PICKLIST_BUCKET_ORDER: FirstMilePicklistEscalationBucketKey[] = [
  "pending_over_30m",
  "slow_pick_over_30m",
];

export const PICKLIST_DRILLDOWN_LABEL: Record<PicklistEscalationDrilldownKey, string> = {
  all: "All picklist escalations",
  ...PICKLIST_BUCKET_LABEL,
};

// Whole-number percentage, 0 when there's nothing to divide by.
export const pct = (num: number, den: number): number => (den > 0 ? Math.round((num / den) * 100) : 0);

// The two completion rates a GRN cell supports:
//   raw      — marked / total, every GRN counted
//   adjusted — the same over ELIGIBLE GRNs only, i.e. with the exclusions taken out of both sides
// Ops read them together: the gap is what the exclusions are costing (or hiding).
export interface GrnRates {
  total: number;
  marked: number;
  rawPct: number;
  eligible: number;
  eligibleMarked: number;
  adjustedPct: number;
  excluded: number;
}

export const grnRates = (cell: FirstMileGrnCell | undefined): GrnRates => {
  const total = cell?.total ?? 0;
  const marked = cell?.marked ?? 0;
  const excluded = cell?.excluded ?? 0;
  const eligible = Math.max(total - excluded, 0);
  const eligibleMarked = Math.max(marked - (cell?.excluded_marked ?? 0), 0);
  return {
    total,
    marked,
    rawPct: pct(marked, total),
    eligible,
    eligibleMarked,
    adjustedPct: pct(eligibleMarked, eligible),
    excluded,
  };
};

// Picklist completion for a cell — no exclusion concept here, so just the one rate.
export const pickRates = (cell: FirstMilePickCell | undefined) => {
  const total = cell?.total ?? 0;
  const picked = cell?.picked ?? 0;
  return { total, picked, pct: pct(picked, total) };
};
